import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import type { StickerInstance } from '../cosmetics/sticker-types';
import type { WeaponSkinLoader } from '../cosmetics/weapon-skin-loader';
import { weaponSkinRegistry } from '../cosmetics/weapon-skin-registry';
import { STICKER_MANIFEST } from '../cosmetics/sticker-types';
import { StickerRenderer } from '../cosmetics/sticker-renderer';
import { WEAPON_MODELS } from '../gameplay/weapon-models';
import { isMobile } from '../core/device';

/**
 * Off-screen renderer for weapon skin previews.
 * Used in inventory UI, marketplace listings, and unboxing reveals.
 *
 * Renders a single weapon with applied skin into a canvas that can be
 * used as an image source or overlaid on the UI.
 */
export class SkinPreviewRenderer {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private light: THREE.DirectionalLight;
  private ambientLight: THREE.AmbientLight;
  private fillLight: THREE.DirectionalLight;
  private canvas: HTMLCanvasElement;

  // Cached models to avoid reloading
  private modelCache = new Map<string, THREE.Group>();
  private loader: GLTFLoader;
  private stickerRenderer = new StickerRenderer();

  // Rotation animation
  private autoRotate = false;
  private rotationSpeed = 0.5; // radians per second
  private currentRotation = 0;

  constructor(width = 512, height = 384) {
    // Create off-screen canvas and renderer
    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    if (isMobile()) {
      this.canvas.style.width = 'clamp(160px, 60vw, 320px)';
      this.canvas.style.height = 'auto';
      this.canvas.style.maxWidth = '100%';
    }

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });
    this.renderer.setSize(width, height);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    // Scene setup
    this.scene = new THREE.Scene();

    // Camera — positioned for optimal weapon viewing angle
    this.camera = new THREE.PerspectiveCamera(40, width / height, 0.01, 10);
    this.camera.position.set(0.3, 0.15, 0.6);
    this.camera.lookAt(0, 0, 0);

    // Lighting — three-point setup for weapon presentation
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(this.ambientLight);

    this.light = new THREE.DirectionalLight(0xfff8f0, 1.0);
    this.light.position.set(2, 3, 2);
    this.scene.add(this.light);

    this.fillLight = new THREE.DirectionalLight(0xc0d0ff, 0.4);
    this.fillLight.position.set(-1, -0.5, 1);
    this.scene.add(this.fillLight);

    // Rim light for edge definition
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.3);
    rimLight.position.set(-1, 1, -2);
    this.scene.add(rimLight);

    // GLTF loader with DRACO
    this.loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
    this.loader.setDRACOLoader(dracoLoader);
  }

  /**
   * Render a weapon with a specific skin to a canvas.
   *
   * @param weaponId - The weapon type (e.g. 'ak47', 'awp')
   * @param skinId - The skin ID to apply (0 = default appearance)
   * @param wearFloat - Wear level 0.0-1.0
   * @param skinLoader - The WeaponSkinLoader for texture application
   * @returns The canvas element containing the rendered preview
   */
  async renderPreview(
    weaponId: string,
    skinId: number,
    wearFloat: number,
    skinLoader: WeaponSkinLoader,
    options?: {
      stickers?: (StickerInstance | null)[];
      nameTag?: string;
    },
  ): Promise<HTMLCanvasElement> {
    // Clear previous scene objects (keep lights)
    const toRemove = this.scene.children.filter(
      (c) => !(c instanceof THREE.Light),
    );
    for (const obj of toRemove) {
      this.scene.remove(obj);
    }

    // Load or get cached weapon model
    const model = await this.loadWeaponModel(weaponId);
    if (!model) {
      // Render empty frame
      this.renderer.render(this.scene, this.camera);
      return this.canvas;
    }

    // Clone model so we don't mutate the cached version
    const clone = model.clone();

    // Center the model
    const box = new THREE.Box3().setFromObject(clone);
    const center = box.getCenter(new THREE.Vector3());
    clone.position.sub(center);

    // Scale to fit view
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim > 0) {
      const scale = 0.4 / maxDim;
      clone.scale.multiplyScalar(scale);
    }

    this.scene.add(clone);

    // Apply skin if specified
    if (skinId > 0) {
      const skin = weaponSkinRegistry.getSkin(skinId);
      if (skin) {
        try {
          const textures = await skinLoader.loadSkinTextures(skin);
          skinLoader.applySkinToMesh(clone, textures, skin, wearFloat);
        } catch {
          // Silently fall back to default appearance
        }
      }
    }

    if (options?.stickers?.some((sticker) => sticker !== null)) {
      await this.stickerRenderer.applyStickers(clone, weaponId, options.stickers, STICKER_MANIFEST);
    }
    if (options?.nameTag?.trim()) {
      this.stickerRenderer.applyNameTag(clone, {
        text: options.nameTag,
        color: 0xd8d19a,
      });
    }

    // Apply current rotation
    clone.rotation.y = this.currentRotation;

    // Render
    this.renderer.render(this.scene, this.camera);

    return this.canvas;
  }

  /**
   * Update auto-rotation animation (call per frame when preview is visible).
   */
  updateRotation(dt: number): void {
    if (!this.autoRotate) return;
    this.currentRotation += this.rotationSpeed * dt;
  }

  /** Enable/disable auto-rotation of the preview model. */
  setAutoRotate(enabled: boolean): void {
    this.autoRotate = enabled;
  }

  /** Set manual rotation angle (radians). */
  setRotation(angle: number): void {
    this.currentRotation = angle;
  }

  /** Resize the preview renderer. */
  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /** Get the canvas element for embedding in UI. */
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  /** Get a data URL of the current preview frame. */
  toDataURL(): string {
    return this.canvas.toDataURL('image/png');
  }

  private async loadWeaponModel(weaponId: string): Promise<THREE.Group | null> {
    // Check cache
    const cached = this.modelCache.get(weaponId);
    if (cached) return cached;

    // Try to load GLB
    const modelConfig = WEAPON_MODELS[weaponId as keyof typeof WEAPON_MODELS];
    if (!modelConfig) return null;

    try {
      const gltf = await this.loader.loadAsync(modelConfig.glbPath);
      this.modelCache.set(weaponId, gltf.scene);
      return gltf.scene;
    } catch {
      return null;
    }
  }

  /** Dispose all resources. */
  dispose(): void {
    this.renderer.dispose();
    for (const model of this.modelCache.values()) {
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    }
    this.modelCache.clear();
  }
}
